// Frontend ARM (role) access helpers.
// Source of truth is backend: /api/users/me/ returns position_code and arm_roles.

const ROLE_SUPERUSER = 'SUPERUSER';

// «Административные» роли (наполняют справочники, редактируют разделы КПС 1-2, 4-5,
// НО не могут составлять план охраны).
export const ROLE_ADMIN = 'ADMIN';

export const ROLE_NZ = 'NZ';
export const ROLE_ZNZ = 'ZNZ';
export const ROLE_ZNZ_VIR = 'ZNZ_VIR';
export const ROLE_STT = 'STT';
export const ROLE_STZ = 'STZ';
export const ROLE_DJ = 'DJ';
export const ROLE_DSS = 'DSS';
export const ROLE_DOTS = 'DOTS';
export const ROLE_WAREHOUSE = 'WAREHOUSE';
export const ROLE_RESPONSIBLE = 'RESPONSIBLE';

export const MODULE_CHPO = 'CHPO';
export const MODULE_KPS = 'KPS';
export const MODULE_HOUSEHOLD = 'HOUSEHOLD';
export const MODULE_BSFP = 'BSFP';
export const MODULE_VIR = 'VIR';
export const MODULE_MPO = 'MPO';

function hasAnyRole(user, roles) {
  if (!user) return false;
  if (user.is_superuser || user.is_staff) return true;
  const userRoles = getArmRoles(user);
  return roles.some((r) => userRoles.includes(r));
}

export function normalizeRole(value) {
  const raw = String(value || '').trim().toUpperCase();
  if (!raw) return '';
  const normalized = raw.replace(/-/g, '_').replace(/\s+/g, '');
  const aliases = {
    // Short Russian abbreviations
    'АДМИН': ROLE_ADMIN,
    'АДМИНИСТРАТОР': ROLE_ADMIN,
    'НЗ': ROLE_NZ,
    'ЗНЗ': ROLE_ZNZ,
    'ЗНЗПОВИР': ROLE_ZNZ_VIR,
    'ЗНЗПО_ВИР': ROLE_ZNZ_VIR,
    'СТТ': ROLE_STT,
    'СТЗ': ROLE_STZ,
    'ДЖ': ROLE_DJ,
    'ДСС': ROLE_DSS,
    'ДОТС': ROLE_DOTS,
    'СКЛАД': ROLE_WAREHOUSE,
    'ОТВЕТСТВЕННЫЙ': ROLE_RESPONSIBLE,
  };

  if (aliases[normalized]) return aliases[normalized];

  // Fuzzy alignment with backend role inference:
  // - "Склад РАВ" / "Склад ГСМ" / etc.
  // - "АРМ Ответственный" / "Ответственный"
  if (normalized.includes('СКЛАД') || normalized.startsWith('АРМСКЛАД')) return ROLE_WAREHOUSE;
  if (normalized.includes('ОТВЕТСТВ')) return ROLE_RESPONSIBLE;

  return normalized;
}

export function getArmRoles(user) {
  if (!user) return [];
  const roles = [];
  if (user.is_superuser) roles.push(ROLE_SUPERUSER);
  if (Array.isArray(user.arm_roles)) roles.push(...user.arm_roles);
  if (user.position_code) roles.push(user.position_code);
  if (user.position?.code) roles.push(user.position.code);
  return [...new Set(roles.map(normalizeRole).filter(Boolean))];
}

const ALL_MODULES = [MODULE_CHPO, MODULE_KPS, MODULE_HOUSEHOLD, MODULE_BSFP, MODULE_VIR, MODULE_MPO];

const MODULES_BY_ROLE = {
  [ROLE_SUPERUSER]: ALL_MODULES,
  // Mini-TZ alignment (module-level gating only; finer-grained rights require backend enforcement)
  [ROLE_NZ]: ALL_MODULES,
  // ZNZ: sees results; works with KPS; has access to warehouse and Responsible ARM
  [ROLE_ZNZ]: [MODULE_CHPO, MODULE_KPS, MODULE_HOUSEHOLD, MODULE_BSFP],
  // ZNZ_VIR: VIR + KPS + warehouse; no MPO mentioned in access matrix
  [ROLE_ZNZ_VIR]: [MODULE_CHPO, MODULE_VIR, MODULE_KPS, MODULE_HOUSEHOLD],
  // STT needs TSOG (/tsog is treated as CHPO module in routing)
  [ROLE_STT]: [MODULE_CHPO, MODULE_KPS, MODULE_HOUSEHOLD],
  [ROLE_STZ]: [MODULE_KPS, MODULE_HOUSEHOLD],
  [ROLE_DJ]: [MODULE_CHPO, MODULE_KPS, MODULE_HOUSEHOLD],
  // DSS: has warehouse responsibilities + sees map/TSOG journals
  [ROLE_DSS]: [MODULE_CHPO, MODULE_KPS, MODULE_HOUSEHOLD],
  // DOTS works with TSOG; give CHPO to access /tsog
  [ROLE_DOTS]: [MODULE_CHPO],
  [ROLE_WAREHOUSE]: [MODULE_HOUSEHOLD],
  // Responsible ARM: primarily map/overview (do not grant extra modules blindly)
  [ROLE_RESPONSIBLE]: [MODULE_CHPO],
};

// По ТЗ 03.09.2026: если у юзера задан permissions_v2 (массив), он —
// авторитетный источник разрешений (см. core/permissions_map.py на бэке).
// Иначе — fallback на arm_roles.
// Модуль viewable если у юзера есть ХОТЬ ОДИН permission с этим префиксом.
const PERMISSION_MODULE_MAP = {
  'chpo':  MODULE_CHPO,
  'kps':   MODULE_KPS,
  'map':   MODULE_CHPO,
  'tsog':  MODULE_CHPO,
  'wms':   MODULE_HOUSEHOLD,
  'bsfp':  MODULE_BSFP,
  'vir':   MODULE_VIR,
  'mpo':   MODULE_MPO,
  'admin': null, // admin permissions не мапятся на модуль сайдбара
};

export function getUserPermissions(user) {
  if (!user) return [];
  if (Array.isArray(user.permissions_v2)) return user.permissions_v2;
  return [];
}

export function hasPermission(user, code) {
  if (!user) return false;
  if (user.is_superuser) return true;
  const perms = getUserPermissions(user);
  return perms.includes(code);
}

export function hasAnyPermission(user, codes) {
  if (!user) return false;
  if (user.is_superuser) return true;
  const perms = new Set(getUserPermissions(user));
  return codes.some((c) => perms.has(c));
}

export function getAllowedModules(user) {
  if (!user) return [];
  if (user.is_superuser || user.is_staff) return ALL_MODULES;

  // Permission-based (приоритет): любой permission с префиксом модуля даёт доступ.
  const perms = getUserPermissions(user);
  if (perms.length) {
    const allowed = new Set();
    for (const code of perms) {
      const prefix = String(code).split('.')[0];
      const mod = PERMISSION_MODULE_MAP[prefix];
      if (mod) allowed.add(mod);
    }
    // Админ-раздел даёт доступ к настройкам, но модули сайдбара только если
    // явно назначены. Если у админа НЕТ ни одного модульного permission —
    // всё равно даём доступ к КПС (справочники) в read-only.
    if (perms.some(p => p.startsWith('admin.'))) {
      // Админ по умолчанию видит все модули (для менеджмента справочников).
      return ALL_MODULES;
    }
    if (allowed.size) return Array.from(allowed);
  }

  // Fallback на roles-based (backward compat).
  const roles = getArmRoles(user);
  const allowed = new Set();
  for (const r of roles) {
    const modules = MODULES_BY_ROLE[r];
    if (Array.isArray(modules)) modules.forEach((m) => allowed.add(m));
    if (String(r || '').toUpperCase().startsWith('SKLAD_')) allowed.add(MODULE_HOUSEHOLD);
  }
  return Array.from(allowed);
}

export function canAccessModule(user, moduleKey) {
  return getAllowedModules(user).includes(moduleKey);
}

export function moduleFromPath(path) {
  if (!path) return null;
  if (path.startsWith('/chpo') || path === '/map-scheme' || path === '/tsog') return MODULE_CHPO;
  if (path.startsWith('/kps')) return MODULE_KPS;
  if (path.startsWith('/household')) return MODULE_HOUSEHOLD;
  if (path.startsWith('/bsfp')) return MODULE_BSFP;
  if (path.startsWith('/vir')) return MODULE_VIR;
  if (path === '/mpo') return MODULE_MPO;
  return null;
}

export function getDefaultPathForUser(user) {
  if (!user) return '/login';
  // По ТЗ «Рабочие задачи» блок 7: суперпользователь — не оператор. Заводим в
  // landing-страницу со ссылкой на Django Admin, а не в /chpo/decision-start.
  if (user.is_superuser) return '/admin-landing';
  const order = [
    { module: MODULE_CHPO, path: '/chpo/decision-start' },
    { module: MODULE_KPS, path: '/kps/map' },
    { module: MODULE_HOUSEHOLD, path: '/household/nomenclatures' },
    { module: MODULE_BSFP, path: '/bsfp/attendance' },
    { module: MODULE_VIR, path: '/vir/performance' },
    { module: MODULE_MPO, path: '/mpo' },
  ];
  for (const it of order) {
    if (canAccessModule(user, it.module) && canAccessPath(user, it.path)) return it.path;
  }
  return '/login';
}

export function canAccessWeaponIssue(user) {
  // КХО / выдача оружия — строго по ТЗ: ДЖ (и НЗ как руководитель).
  return hasAnyRole(user, [ROLE_DJ, ROLE_NZ]);
}

export function canEditKpsSection6(user) {
  // КПС раздел 6 — редактирование только НЗ и ЗНЗ.
  return hasAnyRole(user, [ROLE_NZ, ROLE_ZNZ]);
}

export function canEditTsog(user) {
  // ТСОГ: контроль/заполнение — СТТ, ДОТС (и НЗ).
  return hasAnyRole(user, [ROLE_NZ, ROLE_STT, ROLE_DOTS]);
}

// ============================================================================
// PERMISSIONS: маппинг URL → permission code (read / write).
// По ТЗ 03.09.2026: если у юзера есть permission (или он superuser) — доступ.
// ============================================================================
const PATH_READ_PERM = {
  '/chpo/decision-start': 'chpo.decision.read',
  '/chpo/personnel':      'chpo.personnel.read',
  '/chpo/orders':         'chpo.orders.read',
  '/chpo/map':            'chpo.map.read',
  '/chpo/forces':         'chpo.forces.read',
  '/chpo/brief':          'chpo.brief.read',
  '/chpo/plan':           'chpo.plan.read',
  '/chpo/daily-schedule': 'chpo.schedule.read',
  '/chpo/workload':       'chpo.workload.read',
  '/kps/map':             'kps.read',
  '/kps/personnel':       'kps.read',
  '/kps/orders':          'kps.read',
  '/kps/calendar':        'kps.read',
  '/kps/section-4':       'kps.read',
  '/kps/section-5':       'kps.read',
  '/kps/section-6':       'kps.read',
  '/kps/officers-work':   'kps.read',
  '/kps/inspections':     'kps.read',
  '/kps/dd-members':      'kps.read',
  '/kps/plan-extract':    'kps.read',
  '/kps/border-violations':        'kps.read',
  '/kps/regime-violations':        'kps.read',
  '/kps/border-regime-violations': 'kps.read',
  '/kps/guard-rules-violations':   'kps.read',
  '/bsfp/attendance':     'bsfp.attendance.read',
  '/bsfp/schedule':       'bsfp.schedule.read',
  '/bsfp/literature':     'bsfp.literature.read',
  '/vir/schedule':        'vir.schedule.read',
  '/vir/performance':     'vir.performance.read',
  '/vir/reports':         'vir.reports.read',
  '/mpo':                 'mpo.read',
  '/tsog':                'tsog.read',
  '/map-scheme':          'chpo.map.read',
  '/admin/users':         'admin.users.read',
  '/admin/dictionaries':  'admin.dictionaries.read',
};

const PATH_WRITE_PERM = {
  '/chpo/decision-start': null, // навигационный экран
  '/chpo/personnel':      'chpo.personnel.write',
  '/chpo/orders':         'chpo.orders.write',
  '/chpo/map':            'chpo.map.write',
  '/chpo/forces':         'chpo.forces.write',
  '/chpo/brief':          'chpo.brief.write',
  '/chpo/plan':           'chpo.plan.write',
  '/chpo/daily-schedule': null,
  '/chpo/workload':       null,
  '/kps/map':             'kps.section1.write',
  '/kps/personnel':       'kps.section2.write',
  '/kps/orders':          'kps.section3.write',
  '/kps/section-4':       'kps.section4.write',
  '/kps/section-5':       'kps.section5.write',
  '/kps/section-6':       'kps.section6.write',
  '/kps/officers-work':   'kps.section7.write',
  '/kps/inspections':     'kps.section8.write',
  '/kps/dd-members':      'kps.section9.write',
  '/kps/plan-extract':    'kps.section10.write',
  '/kps/border-violations':        'kps.section11.write',
  '/kps/regime-violations':        'kps.section12.write',
  '/kps/border-regime-violations': 'kps.section13.write',
  '/kps/guard-rules-violations':   'kps.section14.write',
  '/bsfp/attendance':     'bsfp.attendance.write',
  '/bsfp/schedule':       'bsfp.schedule.write',
  '/vir/schedule':        'vir.schedule.write',
  '/vir/performance':     'vir.performance.write',
  '/mpo':                 'mpo.survey.launch',
  '/tsog':                'tsog.write',
  '/map-scheme':          'map.scheme.write',
  '/admin/users':         'admin.users.write',
  '/admin/dictionaries':  'admin.dictionaries.write',
};

function pathReadPermission(path) {
  for (const key of Object.keys(PATH_READ_PERM)) {
    if (path === key || path.startsWith(key + '/')) return PATH_READ_PERM[key];
  }
  return null;
}
function pathWritePermission(path) {
  for (const key of Object.keys(PATH_WRITE_PERM)) {
    if (path === key || path.startsWith(key + '/')) return PATH_WRITE_PERM[key];
  }
  return null;
}

export function canAccessPath(user, path) {
  if (!path) return false;
  if (path === '/login') return true;
  if (user && user.is_superuser) return true;

  // Permission-based (приоритет): если у юзера permissions_v2 задан явно.
  const perms = getUserPermissions(user);
  if (perms.length) {
    const readPerm = pathReadPermission(path);
    if (readPerm && perms.includes(readPerm)) return true;
    // Наличие любой write-permission на пути тоже даёт доступ (write ⇒ read).
    const writePerm = pathWritePermission(path);
    if (writePerm && perms.includes(writePerm)) return true;
    // Path-level restrictions (weapon-issue — жёстко)
    if (path.startsWith('/household/weapon-issue')) return canAccessWeaponIssue(user);
    // Если explicit permission не задан для пути — модульная проверка (для новых страниц)
    if (readPerm === null && writePerm === null) {
      const moduleKey = moduleFromPath(path);
      if (moduleKey && canAccessModule(user, moduleKey)) return true;
    }
    return false;
  }

  // Fallback: roles-based.
  const moduleKey = moduleFromPath(path);
  if (moduleKey && !canAccessModule(user, moduleKey)) return false;
  if (path.startsWith('/household/weapon-issue')) return canAccessWeaponIssue(user);
  return true;
}

// =============================================================================
// RBAC — редактирование разделов по ролям
// =============================================================================
//
// Заказчик определил (встреча 27.08.2026):
//   * Администратор — заполняет справочники, редактирует КПС разделы 1-2 и 4-14,
//     ЧПО (кроме плана охраны, раздела 3), НЕ составляет план охраны.
//   * Начальник заставы (НЗ) — составляет план охраны (CHPO), добавляет
//     прибывших на усиление (номера 41+) в раздел 2 КПС, читает справочники
//     без редактирования.
//
// В перспективе — гибкая RBAC с чекбоксами. Сейчас — простая матрица.

/** Является ли юзер системным администратором (создатель учёток, справочники). */
export function isAdmin(user) {
  if (!user) return false;
  if (user.is_superuser || user.is_staff) return true;
  const roles = getArmRoles(user);
  return roles.includes(ROLE_ADMIN);
}

/** Является ли юзер начальником заставы. */
export function isNZ(user) {
  if (!user) return false;
  if (user.is_superuser) return true; // superuser может всё
  return getArmRoles(user).includes(ROLE_NZ);
}

/**
 * Может ли юзер РЕДАКТИРОВАТЬ раздел по указанному пути.
 * По умолчанию — только чтение.
 *
 * Пути возвращают:
 *   'admin'    → любой админ (справочники, регистрация)
 *   'nz'       → только начальник заставы (план охраны)
 *   'both'     → и админ, и НЗ (общие разделы)
 *   'nz_extra' → админ полностью, НЗ — только «добавить строку на усиление»
 *   'none'     → никто (read-only для всех)
 */
export function canEdit(user, path) {
  if (!user || !path) return false;
  if (user.is_superuser) return true;

  // Permission-based (приоритет): если permissions_v2 задан.
  const perms = getUserPermissions(user);
  if (perms.length) {
    const writePerm = pathWritePermission(path);
    if (writePerm === null) return false; // экран без write-permission — read-only для всех кроме super
    return perms.includes(writePerm);
  }

  // Fallback: roles-based.
  const admin = isAdmin(user);
  const nz = isNZ(user);

  // Справочники и учётки — только админ
  if (path.startsWith('/admin/')) return admin;

  // ЧПО (составление плана охраны) — только НЗ
  if (path.startsWith('/chpo/')) return nz;

  // КПС раздел 2 (список ЛС) — админ полностью, НЗ может дополнять строки усиления
  // (проверка «дополнение vs редактирование» — уже в KpsPersonnelView, здесь только флаг)
  if (path.startsWith('/kps/personnel')) return admin || nz;

  // КПС разделы 4-6 (calendar/section-4|5|6) — только админ
  if (path.startsWith('/kps/section-') || path === '/kps/calendar') return admin;

  // КПС остальные разделы (приказы, инспекции, ДД, план-выписка, нарушения, оф.работы)
  // — админ + НЗ (оба редактируют)
  if (path.startsWith('/kps/')) return admin || nz;

  // Схема участка — админ (реализация карты — админский раздел)
  if (path === '/map-scheme') return admin || nz;

  // Военное хозяйство — по своим правилам
  if (path.startsWith('/household/')) {
    if (path.startsWith('/household/weapon-issue')) return canAccessWeaponIssue(user);
    return admin || hasAnyRole(user, [ROLE_WAREHOUSE, ROLE_NZ]);
  }

  // МПО — админ создаёт опрос, kiosk — публично
  if (path === '/mpo') return admin || nz;

  // По умолчанию — можно редактировать (обратная совместимость)
  return true;
}
